import { Injectable } from '@angular/core';
import { EMPTY, Observable, Observer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Injectable()
export class ImageService {
  pica: pica;

  constructor() {
    this.pica = new pica();
  }

  getDataUrlFromFile(file: File): Observable<string> {
    if (file == null) {
      return EMPTY;
    }

    return new Observable((obs: Observer<any>) => {
      const reader = new FileReader();
      reader.onerror = err => obs.error(err);
      reader.onabort = err => obs.error(err);
      reader.onload = () => obs.next(reader.result);
      reader.onloadend = () => obs.complete();

      return reader.readAsDataURL(file);
    });
  }

  getImageFromDataurl(url: string): Observable<HTMLImageElement> {
    if (!url) {
      return EMPTY;
    }

    return new Observable((obs: Observer<any>) => {
      const image = new Image();
      image.onerror = err => obs.error(err);
      image.onabort = err => obs.error(err);
      image.onload = () => {
        obs.next(image);
        obs.complete();
      };
      image.src = url;
    });
  }

  getImageFromFile(file: File): Observable<HTMLImageElement> {
    if (!file) {
      return EMPTY;
    }

    return this.getDataUrlFromFile(file)
      .pipe(mergeMap(dataUrl => {
        return this.getImageFromDataurl(dataUrl);
      }));
  }

  blobToFile(blob: Blob, name: string): File {
    const anyFile: any = blob;
    anyFile.name = name;
    anyFile.lastModified = new Date().getTime();
    return anyFile;
  }

  public resizeImage(image: HTMLImageElement, width: number, height: number, fileName: string, fileType: string): Observable<File> {
    if (image == null) {
      return EMPTY;
    }

    return new Observable((obs: Observer<any>) => {
      const originCanvas = document.createElement('canvas');
      const ctx = originCanvas.getContext('2d');
      originCanvas.width = image.width;
      originCanvas.height = image.height;
      ctx.drawImage(image, 0, 0);

      const destinationCanvas = document.createElement('canvas');
      destinationCanvas.width = width;
      destinationCanvas.height = height;

      this.picaResize(originCanvas, destinationCanvas, undefined, fileName, fileType)
        .catch(function (err) { obs.error(err); })
        .then(file => {
          obs.next(file);
        });
    });
  }

  private picaResize(from: HTMLCanvasElement, to: HTMLCanvasElement, options: any, fileName: string, fileType: string): Promise<File> {
    const self = this;
    return new Promise(function (resolve, reject) {
        self.pica.resize(from, to, options)
            .catch(function (err) { return reject(err); })
            .then(function (resizedCanvas: HTMLCanvasElement) { return self.pica.toBlob(resizedCanvas, fileType); })
            .then(function (blob) {
            const fileResized = self.blobToFile(blob, fileName);
            resolve(fileResized);
        });
    });
  }

  public getOrientedImage(image: HTMLImageElement): Observable<HTMLImageElement> {
    if (!image) {
      return EMPTY;
    }

    return new Observable((obs: Observer<any>) => {
      let dataIsSucces = false;
      try {
        EXIF.getData(image, () => {
          const orientation = EXIF.getTag(image, 'Orientation');
          dataIsSucces = true;

          if (!orientation) {
            obs.next(image);
            obs.complete();
          } else {
            this.processImageWithOrientation(orientation, image, obs);
          }
        });
      } catch (error) {
        if (!dataIsSucces) {
          obs.next(image);
          obs.complete();
        } else {
          obs.error(error);
        }
      }
    });
  }

  private processImageWithOrientation(orientation: number, image: HTMLImageElement, obs: any) {
    if (!image || !obs) {
      return;
    }

    let img = null;
    if (orientation !== 1) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      let cw: number = image.width;
      let ch: number = image.height;
      let cx = 0;
      let cy = 0;
      let deg = 0;
      switch (orientation) {
        case 3:
        case 4:
          cx = -image.width;
          cy = -image.height;
          deg = 180;
          break;
        case 5:
        case 6:
          cw = image.height;
          ch = image.width;
          cy = -image.height;
          deg = 90;
          break;
        case 7:
        case 8:
          cw = image.height;
          ch = image.width;
          cx = -image.width;
          deg = 270;
          break;
        default:
          break;
      }
      canvas.width = cw;
      canvas.height = ch;
      if ([2, 4, 5, 7].indexOf(orientation) > -1) {
        // flip image
        ctx.translate(cw, 0);
        ctx.scale(-1, 1);
      }
      if (deg !== 0) {
        ctx.rotate(deg * Math.PI / 180);
      }
      ctx.drawImage(image, cx, cy);
      img = new Image();
      img.width = cw;
      img.height = ch;
      img.onload = () => {
        obs.next(img);
        obs.complete();
      };
      img.src = canvas.toDataURL('image/png');
    } else {
      obs.next(image);
      obs.complete();
    }
  }
}
